import { useEffect } from 'react';
import { Box, Flex, Grid } from 'components/box';
import { Field, Form } from 'react-final-form';
import { Link, Text } from 'components/typography';
import { closeDrawer, openDrawer } from '../../redux/global/actions';
import { useDispatch, useSelector } from 'react-redux';
import { useLogin } from 'hooks/customer';
import Button from 'components/button';
import Divider from './divider';
import LabeledInput from 'components/form/input';
import createDecorator from 'final-form-focus';
import isEmail from 'sane-email-validation';
import { ErrorBox } from './error-box';
import { RootState } from 'typesafe-actions';
import SectionTitle from 'components/_shared/elements/section-title';
import { css } from '@emotion/react';
import { useVodapayActive } from 'hooks/app';
import VodapayAuth from 'components/_shared/widgets/vodapay-authentication';
import { AuthenticationContext } from 'types/types';
import SocialAuth from './social-auth';
import Password from 'components/form/password';

const focusOnErrors = createDecorator();

interface LoginProps {
  createBtnOnClick?: () => void;
  forgotPasswordOnClick?: () => void;
  showCloseBtn?: boolean;
  headerText: string;
  showCreateAccountBtn?: boolean;
  showFirstTimeCustomersMessage?: boolean; // TODO: not being used, but might come back after MAG-308
}

const Login = ({
  createBtnOnClick,
  forgotPasswordOnClick,
  showCloseBtn,
  headerText,
  showCreateAccountBtn,
}: LoginProps) => {
  const dispatch = useDispatch();
  const loginMessage = useSelector(
    (state: RootState) => state.global.loginMessage
  );

  const [login, { loading, error, completed }] = useLogin();
  const close = () => dispatch(closeDrawer());

  useEffect(() => {
    if (completed) {
      dispatch(closeDrawer());
    }
  }, [dispatch, completed]);

  return (
    <>
      <SectionTitle
        fontSize={[3, 4]}
        title={headerText}
        closeButtonCallback={showCloseBtn ? close : undefined}
      />

      {loginMessage && <ErrorBox errors={[loginMessage]} />}
      {error && error.graphQLErrors.length > 0 && (
        <ErrorBox errors={error.graphQLErrors.map(e => e.message)} />
      )}

      <Form
        decorators={[focusOnErrors]}
        onSubmit={({ email, password }) => login({ email, password })}
      >
        {({ handleSubmit }) => (
          <form onSubmit={handleSubmit} noValidate>
            <Grid mt="4">
              <Field
                name="email"
                validate={value => {
                  if (!value) {
                    return 'Enter your email';
                  }
                  if (!isEmail(value)) {
                    return 'Enter a valid email';
                  }
                }}
              >
                {({ input, meta }) => (
                  <Box>
                    <LabeledInput
                      {...input}
                      label="Email Address"
                      type="email"
                      error={meta.touched && meta.error}
                      invalid={meta.error && meta.touched}
                      placeholder="Email"
                    />
                  </Box>
                )}
              </Field>

              <Field
                name="password"
                validate={value => {
                  if (!value) {
                    return 'Enter your password';
                  }
                }}
              >
                {({ input, meta }) => (
                  <Box>
                    <Password
                      label="Password"
                      placeholder="Password"
                      {...input}
                      invalid={meta.error && meta.touched}
                      error={meta.touched && meta.error}
                    />
                  </Box>
                )}
              </Field>

              <Flex justifyContent="flex-end">
                <Button
                  p="0"
                  fontSize="2"
                  variant="flat"
                  display="inline-block"
                  onClick={() => {
                    forgotPasswordOnClick
                      ? forgotPasswordOnClick()
                      : dispatch(openDrawer('forgot'));
                  }}
                >
                  Forgot password?
                </Button>
              </Flex>

              <Button
                width="100%"
                mb="2"
                py="0.8rem"
                loading={loading}
                disabled={loading}
                type="submit"
                css={theme => css`
                  box-shadow: ${theme.shadows.authButton};
                `}
              >
                Log In
              </Button>

              <Box>
                <Divider text="Or" mb="1" />
              </Box>

              <SocialAuth context={AuthenticationContext.LogIn} />

              {showCreateAccountBtn && (
                <Text textAlign="center" fontSize="2" my="2">
                  Don&apos;t have an account?{' '}
                  <Button
                    p="0"
                    variant="flat"
                    display="inline-block"
                    textTransform="none"
                    lineHeight={0}
                    onClick={() =>
                      createBtnOnClick
                        ? createBtnOnClick()
                        : dispatch(openDrawer('createAccount'))
                    }
                    css={theme => css`
                      letter-spacing: 0px;
                      font-family: ${theme.fonts.body};
                    `}
                  >
                    Create Account
                  </Button>
                </Text>
              )}
            </Grid>
          </form>
        )}
      </Form>
    </>
  );
};

const Wrapper = (props: LoginProps) => {
  const vodapayIsActive = useVodapayActive();
  if (vodapayIsActive) {
    return <VodapayAuth />;
  }

  return <Login {...props} />;
};

export default Wrapper;
